// ============================================================================
// MERGE SORT CLASSIC TREE RENDERER
// Reconstructs the recursive divide/merge tree from execution steps and draws
// the classic YouTube-style multi-level block diagram:
//   top row = whole array
//   next rows = recursive halves
//   bottom = single elements
//   then merges back up to the top.
// ============================================================================

import type { ExecutionStep } from '../../../../core/types/rust-engine-interfaces';

export interface MergeTreeNode {
  range: [number, number];     // inclusive indices in the original array
  depth: number;               // depth from root (0 = full array)
  parent: MergeTreeNode | null;
  children: MergeTreeNode[];
  values: number[];            // snapshot of this sub-array at the relevant step
  state: 'default' | 'active' | 'merged' | 'sorted';
  // Runtime layout fields assigned by the renderer.
  x: number;
  width: number;
}

export interface MergeTreeLayout {
  nodes: MergeTreeNode[];
  levels: number;              // number of distinct depth levels (split + merge halves)
  maxDepth: number;            // deepest split depth
  nodeMap: Map<string, MergeTreeNode>; // key = L..R
  root: MergeTreeNode;
}

export function nodeKey(L: number, R: number): string { return `${L}..${R}`; }

/** Build the complete binary split/merge tree from the array length. */
export function buildMergeTree(n: number): MergeTreeLayout {
  const nodeMap = new Map<string, MergeTreeNode>();
  const nodes: MergeTreeNode[] = [];

  const build = (L: number, R: number, depth: number, parent: MergeTreeNode | null): MergeTreeNode => {
    const key = nodeKey(L, R);
    let node = nodeMap.get(key);
    if (!node) {
      node = {
        range: [L, R],
        depth,
        parent,
        children: [],
        values: Array.from({ length: R - L + 1 }, (_, i) => i), // placeholder values
        state: 'default',
        x: 0,
        width: 0,
      };
      nodeMap.set(key, node);
      nodes.push(node);
    }
    if (L < R && node.children.length === 0) {
      const mid = Math.floor((L + R) / 2);
      const left = build(L, mid, depth + 1, node);
      const right = build(mid + 1, R, depth + 1, node);
      node.children.push(left, right);
    }
    return node;
  };

  const root = build(0, n - 1, 0, null);
  const maxDepth = Math.max(...nodes.map(n => n.depth));
  // The classic tree has split levels going down, then merge levels coming back up.
  // For layout, we use 2*maxDepth + 1 rows: root at top, split down, leaves at bottom,
  // merge rows going back up. But we can merge duplicate levels visually.
  const levels = maxDepth * 2 + 1;
  return { nodes, levels, maxDepth, nodeMap, root };
}

/** Find the merge tree node whose range fully contains the highlighted indices. */
export function findActiveNode(
  layout: MergeTreeLayout,
  currentStep: ExecutionStep,
): MergeTreeNode | null {
  const meta = currentStep.payload.type === 'array'
    ? currentStep.payload.metadata
    : {};

  // 1. Best: explicit mergeRange from generator metadata.
  if (meta.mergeRange) {
    const [L, R] = meta.mergeRange;
    const node = layout.nodeMap.get(nodeKey(L, R));
    if (node) return node;
  }

  // 2. Fallback: parse [L..R] from explanation text.
  const explanation = currentStep.metadata?.explanation ?? '';
  const divMatch = explanation.match(/\[(-?\d+)\.\.(-?\d+)\]/);
  if (divMatch) {
    const L = parseInt(divMatch[1]!, 10);
    const R = parseInt(divMatch[2]!, 10);
    const node = layout.nodeMap.get(nodeKey(L, R));
    if (node) return node;
  }

  // 3. Last resort: smallest range containing all highlighted indices.
  const highlight = meta.highlightIndices ?? meta.compareIndices ?? [];
  const current = meta.currentIndex;
  const targetIndices = new Set<number>(
    [...highlight, current].filter((i): i is number => typeof i === 'number'),
  );
  if (targetIndices.size === 0) return null;

  let best: MergeTreeNode | null = null;
  let bestSize = Infinity;
  for (const node of layout.nodes) {
    const [L, R] = node.range;
    const covers = [...targetIndices].every(i => i >= L && i <= R);
    if (covers && R - L + 1 < bestSize) {
      bestSize = R - L + 1;
      best = node;
    }
  }
  return best;
}

export function updateTreeValues(
  layout: MergeTreeLayout,
  values: number[],
  activeNode: MergeTreeNode | null,
): void {
  // Fill every node with the current slice of the main array.
  for (const node of layout.nodes) {
    const [L, R] = node.range;
    node.values = values.slice(L, R + 1);
  }

  // Mark states for the active path.
  for (const node of layout.nodes) {
    node.state = 'default';
  }
  if (activeNode) {
    activeNode.state = 'active';
    let p: MergeTreeNode | null = activeNode.parent;
    while (p) {
      if (p.state === 'default') p.state = 'merged';
      p = p.parent;
    }
    // Mark leaves under the active node as 'merged' (but not the active node itself).
    const markLeaves = (n: MergeTreeNode) => {
      if (n === activeNode) return;
      if (n.children.length === 0) {
        n.state = 'merged';
      } else {
        n.children.forEach(markLeaves);
      }
    };
    markLeaves(activeNode);
  }
}
